import React from 'react';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { GradientBar } from '../../atoms/GradientBar';
import { Icon } from '../../atoms/Icon';
import { Link } from '../../atoms/Link';
import { Text } from '../../atoms/Text';
import { Button } from '../../atoms/Button';
import { FooterSection, FooterLink } from '../../molecules/FooterSection';
import heartInternetLogo from '../../assets/images/heart-internet-logo.png';
import './Footer.css';

export interface FooterLinkSection {
  title: string;
  links: FooterLink[];
}

export interface SocialLink {
  platform: 'facebook' | 'linkedin' | 'x';
  href: string;
}

export interface FooterProps {
  /**
   * Link sections to display (e.g. Services, Company)
   */
  sections: FooterLinkSection[];
  /**
   * Social media links
   */
  socialLinks: SocialLink[];
  /**
   * Blog link configuration
   */
  blogLink?: { label: string; href: string };
  /**
   * Domain to prefix for links
   */
  wwwDomain?: string;
  /**
   * Show gradient bar at top of footer
   */
  showGradientBar?: boolean;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
}

export const Footer: React.FC<FooterProps> = ({
  sections,
  socialLinks,
  blogLink,
  wwwDomain = 'www.heartinternet.uk',
  showGradientBar = true,
  className = '',
  style,
}) => {
  const year = new Date().getFullYear();
  const baseClass = 'hi-ui hi-ui-footer';
  const allClasses = `${baseClass} ${className}`.trim();
  const protocol = 'https';

  return (
    <footer id="hi-ui-footer" className={allClasses} style={style}>
      {showGradientBar && <GradientBar />}
      <LayoutContainer>
        <div className="hi-ui-footer__content">
          <div className="hi-ui-footer__logo">
            <img src={heartInternetLogo} alt="Heart Internet" />
          </div>

          <div className="hi-ui-footer__sections">
            {sections.map((section) => (
              <FooterSection
                key={section.title}
                title={section.title}
                links={section.links}
                wwwDomain={wwwDomain}
              />
            ))}
          </div>

          <div className="hi-ui-footer__social">
            <div className="hi-ui-footer__social-icons">
              {socialLinks.map((link) => (
                <Link
                  key={link.platform}
                  href={link.href}
                  external
                  noUnderline
                  aria-label={link.platform}
                  className="hi-ui-footer__social-link"
                >
                  <Icon name={link.platform} size={35} />
                </Link>
              ))}
            </div>
            {blogLink && (
              <Button
                as="a"
                href={`${protocol}://${wwwDomain}${blogLink.href}`}
                variant="success"
                size="small"
              >
                {blogLink.label}
              </Button>
            )}
          </div>
        </div>

        <div className="hi-ui-footer__legal">
          <div className="hi-ui-footer__legal-left">
            <Text variant="body-small" inline className="hi-ui-footer__copyright">
              {String.fromCharCode(169)} {year} Heart Internet Ltd. All rights reserved.
            </Text>
            <div className="hi-ui-footer__legal-links">
              <Link href={`${protocol}://${wwwDomain}/terms`} noUnderline>
                Terms and conditions
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link
                href="#"
                className="termly-display-preferences"
                noUnderline
                onClick={(e) => {
                  e.preventDefault();
                  // Termly consent modal - try multiple API methods
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const w = window as any;
                  if (w.displayPreferenceModal) {
                    w.displayPreferenceModal();
                  } else if (w.Termly?.displayPreferenceModal) {
                    w.Termly.displayPreferenceModal();
                  }
                }}
              >
                Consent Preferences
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link
                href={`${protocol}://${wwwDomain}/terms/heart-internet-privacy-statement`}
                noUnderline
              >
                Privacy Policy
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link href={`${protocol}://${wwwDomain}/privacy-centre`} noUnderline>
                Privacy Centre
              </Link>
              <span className="hi-ui-footer__legal-separator">-</span>
              <Link href={`${protocol}://${wwwDomain}/sitemap`} noUnderline>
                Sitemap
              </Link>
            </div>
          </div>
          <div className="hi-ui-footer__legal-right">
            <Text variant="body-small" inline>
              VAT Number: 467683539
            </Text>
            <Text variant="body-small" inline>
              Company number: 15319281
            </Text>
          </div>
        </div>
      </LayoutContainer>
    </footer>
  );
};
